<template>
  <el-dialog
    :visible.sync="dialogVisible"
    width="50%"
    :show-close="false"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
  >
    <div class="close-bar">
      <i
        class="icon-close"
        @click="close(); $sendGAEvent('AD_modal_close', { value: adModalData.Title })"
      />
    </div>
    <div v-if="adModalData" class="ad-modal">
      <div class="ad-modal-image" :style="{'background-image': `url(${adModalData.ImageUrl})`}" />
      <div class="ad-modal-context">
        <div class="ad-modal__title">
          {{ adModalData.Title }}
        </div>
        <div class="ad-modal__sub-title">
          {{ adModalData.Subtitle }}
        </div>
        <div class="ad-modal__btn-group">
          <div v-if="adModalData.CtaText && adModalData.CtaLink">
            <el-button
              type="primary"
              size="large"
              @click="handleCTA(adModalData); $sendGAEvent('AD_modal_CTA', { value: adModalData.Title })"
            >
              {{ adModalData.CtaText }}
            </el-button>
          </div>
          <div>
            <el-button
              type="text"
              size="large"
              class="transparent-btn"
              @click="close(adModalData); $sendGAEvent('AD_modal_got_it', { value: adModalData.Title })"
            >
              {{ $t('AD.TodayNotShowAgain') }}
            </el-button>
          </div>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { mapState } from 'vuex'
import { housekeeperAPI } from '@/api/HousekeeperAPI'
import adModalEmptyImg from '@/assets/img/ad/adModal-empty.png'

export default {
  data () {
    return {
      isShowAdModal: false,
      dialogVisible: false,
      adModalData: null
    }
  },
  computed: {
    ...mapState('advertising', ['adModalStatus'])
  },
  watch: {
    isShowAdModal () {
      this.dialogVisible = this.isShowAdModal && !!this.adModalData
    },
    adModalData () {
      this.dialogVisible = this.isShowAdModal && !!this.adModalData
    }
  },
  async created () {
    this.initAdModalStatus()
    this.isShowAdModal = this.checkIsShowAdModal()
    await this.fetchAd()
  },
  methods: {
    initAdModalStatus () {
      if (this.adModalStatus) return
      try {
        const adModalStatusStorage = localStorage.getItem('adModalStatus')
        if (!adModalStatusStorage) throw new Error('localStorage adModalStatus is empty')
        const adModalStatus = JSON.parse(adModalStatusStorage)
        // 定期清除已經過期的 closeAd，避免持續累積
        if (adModalStatus?.closeAdIds?.length > 0) {
          adModalStatus.closeAdIds = adModalStatus.closeAdIds.filter((item) => new Date(item.endDate) > new Date())
        }
        this.$store.commit('advertising/setAdModalStatus', adModalStatus)
      } catch (e) {
        const adModalStatus = {
          hideExpirationDate: null, // 隱藏結束日期
          closeAdIds: [] // 已關閉的廣告 id，包含 id, endDate 廣告過期日
        }
        this.$store.commit('advertising/setAdModalStatus', adModalStatus)
      }
    },
    checkIsShowAdModal () {
      const loginUserInfoJSON = localStorage.getItem('loginUserInfo')
      if (!loginUserInfoJSON) return false
      try {
        const loginUserInfo = JSON.parse(loginUserInfoJSON)
        // EIP / 一次性密碼登入 / 非實名制帳號 則不顯示廣告
        if (loginUserInfo.IsFromEip || loginUserInfo.IsOneTimeAccount || !loginUserInfo.IsAuthorizationAccount) return false
      } catch (e) {
        return false
      }
      if (!this.adModalStatus) return false // adModalStatus 還沒同步完成
      if (!this.adModalStatus.hideExpirationDate) return true // 未設定 hideExpirationDate 代表完全還沒看過廣告
      const today = new Date()
      const hideExpirationDate = new Date(this.adModalStatus.hideExpirationDate)
      if (today < hideExpirationDate) return false // 隱藏還沒過期
      return true
    },
    async fetchAd () {
      try {
        const res = await housekeeperAPI.getAdModal()
        if (res?.data?.Data?.Modals) {
          const closeAdIds = this.adModalStatus.closeAdIds.map((item) => item.id)
          const filterCanShowAdModals = res.data.Data.Modals.filter(item => {
            const today = new Date()
            return !closeAdIds.includes(item.Id) && today >= new Date(item.StartTimeUtc) && today <= new Date(item.EndTimeUtc)
          })
          if (filterCanShowAdModals.length > 0) {
            const adModals = this.sortAdModal(filterCanShowAdModals)
            const adModal = adModals[0]
            adModal.ImageUrl = await this.checkImage(adModal.ImageUrl)
            this.adModalData = adModal
          } else this.adModalData = null
        } else this.adModalData = null
      } catch (e) {
        console.log(e)
      }
    },
    /**
     * 排序 ad modal。如果是置頂，則新到舊，一樣新則照 componentId 排序；否則隨機（照 componentId 排序）
     */
    sortAdModal (adModalsData) {
      const topAdModals = adModalsData.filter((item) => item.IsTop)
      const notTopAdModals = adModalsData.filter((item) => !item.IsTop)
      const sortedTopAdModals = topAdModals.sort((a, b) => {
        if (new Date(a.StartTimeUtc) > new Date(b.StartTimeUtc)) return -1
        else if (new Date(a.StartTimeUtc) < new Date(b.StartTimeUtc)) return 1
        else if (a.Id > b.Id) return -1
        else return 1
      })
      const sortedNotTopAdModals = notTopAdModals.sort((a, b) => {
        if (a.Id > b.Id) return -1
        else return 1
      })

      return [...sortedTopAdModals, ...sortedNotTopAdModals]
    },
    close (adModal) {
      this.isShowAdModal = false
      const newAdModalStatus = JSON.parse(JSON.stringify(this.adModalStatus))
      newAdModalStatus.hideExpirationDate = dayjs().add(1, 'day').format('YYYY/MM/DD') // 隱藏一天
      if (adModal) {
        newAdModalStatus.closeAdIds.push({
          id: adModal.Id,
          endDate: adModal.EndTimeUtc
        }) // 不再顯示這則廣告
      }
      this.$store.commit('advertising/setAdModalStatus', newAdModalStatus)
      localStorage.setItem('adModalStatus', JSON.stringify(newAdModalStatus))
    },
    handleCTA (adModal) {
      // 前往頁面
      this.close(adModal)
      window.open(adModal.CtaLink)
    },
    async checkImage (imageUrl) {
      const downloadingImage = new Image()
      return new Promise((resolve) => {
        downloadingImage.onload = () => {
          resolve(imageUrl)
        }
        downloadingImage.onerror = () => {
          resolve(adModalEmptyImg)
        }
        downloadingImage.src = imageUrl
      })
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .el-dialog {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: transparent;
  border-radius: unset;
  box-shadow: unset;
  margin-top: unset !important;
  margin-bottom: unset !important;
  .el-dialog__header {
    padding: 0 !important;
  }
  .el-dialog__body {
    padding: unset !important;
  }
}
.close-bar {
  text-align: right;
  padding-block: 12px;
  i {
    cursor: pointer;
    color: $white;
    font-size: map-get($fontSize, x-large);
  }
}
.ad-modal {
  background-color: $white;
  border-radius: 8px;
  padding: 24px;
  .ad-modal-image {
    border-radius: 8px;
    background-size: cover;
    background-position: center;
    aspect-ratio: 752 / 330;
    @supports not (aspect-ratio: 752/330) {
      padding-top: calc(330 / 752 * 100%);
    }
  }
  .ad-modal-context {
    margin-top: 24px;
    padding-inline: 24px;
    text-align: center;
    .ad-modal__title {
      @include word-break-line(2);
      font-size: map-get($fontSize, xxxx-large);
      font-weight: 600;
      color: $fontColor;
      line-height: 40px;
    }
    .ad-modal__sub-title {
      @include word-break-line(2);
      margin-top: 14px;
      font-size: map-get($fontSize, large);
      color: $hintColor;;
    }
    .ad-modal__btn-group {
      margin-top: 24px;
      & > div:not(:last-child) {
        margin-bottom: 8px;
      }
      .transparent-btn {
        color: $fontColor;
        ::v-deep span {
          border: unset;
        }
        &:hover {
          color: $subFontColor;
        }
        &:active {
          color: $fontColor;
        }
      }
    }
  }
}
</style>
